import { useRef, useState, type DragEvent } from "react";
import { Alert, Badge, Button, Card, EmptyState, Icon, Input } from "../../ds";
import { ApiError, api, type FirmwareCheck, type ImageMeta, type Model } from "../../lib/api";
import { fmtBytes } from "../../lib/format";

// Upload eines fertigen sysupgrade-Images in den Katalog. Gebaut werden die
// Images in den Firmware-Repos; hier landen sie nur — der Server liest aus dem
// Image, was es ist, und prüft es gegen das gewählte Modell.

/** Antwort des Servers, wenn er den Upload abgelehnt hat. */
interface Rejection {
  message: string;
  checks?: FirmwareCheck[];
  meta?: ImageMeta;
  /** Der Konflikt lässt sich mit ausdrücklicher Bestätigung übergehen. */
  canForce?: boolean;
  /** Image ohne OpenWrt-Metadaten — die Version muss von Hand kommen. */
  needsVersion?: boolean;
}

const CHECK_TONE = {
  ok: { variant: "success" as const, icon: "check" as const },
  mismatch: { variant: "danger" as const, icon: "close" as const },
  unknown: { variant: "neutral" as const, icon: "help" as const },
};

/** Die Prüfpunkte als Liste — was erwartet wurde, was im Image steht. */
export function CheckList({ checks }: { checks: FirmwareCheck[] }) {
  return (
    <ul className="flex flex-col gap-1.5">
      {checks.map((c) => {
        const tone = CHECK_TONE[c.status] ?? CHECK_TONE.unknown;
        return (
          <li key={c.key} className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant={tone.variant} dot>
              {c.label}
            </Badge>
            <span className="font-mono text-xs text-content-2">{c.actual || "—"}</span>
            {c.status === "mismatch" && (
              <span className="text-xs text-content-3">erwartet: {c.expected}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Ablagefläche für die Datei — Klick öffnet den Dateidialog, Ziehen tut es auch. */
function DropZone({ file, onPick }: { file: File | null; onPick: (f: File | null) => void }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div
      onDragOver={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragging(false);
        onPick(e.dataTransfer.files?.[0] ?? null);
      }}
      onClick={() => inputRef.current?.click()}
      className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-dashed p-6 text-center transition-colors ${
        dragging ? "border-primary bg-hover-bg" : "border-line hover:border-line-strong"
      }`}
    >
      <Icon name="upload" size={22} className="text-content-3" />
      {file ? (
        <>
          <span className="font-mono break-all text-sm text-content">{file.name}</span>
          <span className="text-xs text-content-3">{fmtBytes(file.size)}</span>
        </>
      ) : (
        <>
          <span className="text-sm text-content-2">Image hierher ziehen oder auswählen</span>
          <span className="text-xs text-content-3">*.bin aus dem Firmware-Build</span>
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".bin,.img,application/octet-stream"
        className="hidden"
        onChange={(e) => onPick(e.target.files?.[0] ?? null)}
      />
    </div>
  );
}

/** Was der Server bemängelt hat — samt Ausweg, wenn es einen gibt. */
function RejectionNotice({
  rejection,
  busy,
  onForce,
}: {
  rejection: Rejection;
  busy: boolean;
  onForce: () => void;
}) {
  return (
    <Alert
      variant={rejection.canForce ? "warning" : "danger"}
      title={rejection.canForce ? "Image passt nicht zum Modell" : "Upload abgelehnt"}
    >
      <div className="flex flex-col gap-3">
        <span>{rejection.message}</span>
        {rejection.checks && <CheckList checks={rejection.checks} />}
        {rejection.canForce && (
          <div>
            <Button
              size="sm"
              variant="danger-outline"
              iconName="upload"
              loading={busy}
              onClick={onForce}
            >
              Trotzdem übernehmen
            </Button>
            <p className="mt-2 text-xs text-content-3">
              Ein Image für ein anderes Gerät macht den Router beim Einspielen aus der Ferne
              unbrauchbar. Die Warnung bleibt am Katalogeintrag stehen.
            </p>
          </div>
        )}
      </div>
    </Alert>
  );
}

/** Die Angaben neben der Datei. Modell ist Pflicht, die Bauart unterscheidet
 *  Images, die sonst nur am sha256 auseinanderzuhalten wären. Die Version wird
 *  erst gefragt, wenn das Image selbst keine trägt. */
function Felder(props: {
  models: Model[] | null;
  modelId: string;
  onModel: (id: string) => void;
  tag: string;
  onTag: (v: string) => void;
  version: string;
  onVersion: (v: string) => void;
  versionNoetig: boolean;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <label className="flex flex-col gap-1.5">
        <span className="text-xs text-content-3">Modell</span>
        <select
          className="ds-input"
          value={props.modelId}
          onChange={(e) => props.onModel(e.target.value)}
        >
          <option value="">— auswählen —</option>
          {(props.models ?? []).map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      </label>
      <Input
        label="Bauart"
        value={props.tag}
        onChange={(e) => props.onTag(e.target.value)}
        placeholder="eulektro, generic, swb"
        helperText="Leer = aus dem Bau-Stempel des Dateinamens."
      />
      {props.versionNoetig && (
        <Input
          label="Version (das Image trägt keine)"
          value={props.version}
          onChange={(e) => props.onVersion(e.target.value)}
          placeholder="z.B. 25.12.5"
        />
      )}
    </div>
  );
}

interface FirmwareUploadProps {
  models: Model[] | null;
  onUploaded: () => void;
}

/** Die usign-.sig zum Image. Ohne sie lehnt der Router das Flashen ab. */
function SignatureField({
  signature,
  onPick,
}: {
  signature: File | null;
  onPick: (f: File | null) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor="fw-sig" className="text-sm text-content-2">
        usign-Signatur (.sig)
        <span className="text-content-3"> — ohne sie lehnt der Router das Flashen ab</span>
      </label>
      <input
        id="fw-sig"
        type="file"
        accept=".sig,.bin.sig,application/octet-stream"
        className="ds-input"
        onChange={(e) => onPick(e.target.files?.[0] ?? null)}
      />
      {signature && <span className="text-xs text-content-3">{signature.name}</span>}
    </div>
  );
}

export function FirmwareUpload({ models, onUploaded }: FirmwareUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [signature, setSignature] = useState<File | null>(null);
  const [modelId, setModelId] = useState("");
  const [version, setVersion] = useState("");
  const [tag, setTag] = useState("");
  const [busy, setBusy] = useState(false);
  const [rejection, setRejection] = useState<Rejection | null>(null);
  const [done, setDone] = useState<string | null>(null);

  function pick(next: File | null) {
    setFile(next);
    // Jede neue Datei ist ein neuer Fall — alte Urteile dürfen nicht stehen
    // bleiben, sonst bestätigt jemand eine Warnung, die zur vorigen Datei gehört.
    setRejection(null);
    setDone(null);
    setVersion("");
    setSignature(null);
  }

  async function upload(force: boolean) {
    if (!file || !modelId) return;
    setBusy(true);
    setDone(null);
    try {
      const fw = await api.uploadFirmware(file, modelId, {
        version: version.trim(),
        tag: tag.trim(),
        force,
        signature,
      });
      setDone(`${fw.version} liegt im Katalog.`);
      pick(null);
      onUploaded();
    } catch (e) {
      if (e instanceof ApiError) {
        // Der Body ist der Kontrakt aus core/views.py::_firmware_upload.
        const body = e.body as {
          error?: string;
          checks?: FirmwareCheck[];
          meta?: ImageMeta;
          canForce?: boolean;
          needsVersion?: boolean;
        };
        setRejection({
          message: body.error ?? "Upload abgelehnt.",
          checks: body.checks,
          meta: body.meta,
          canForce: body.canForce,
          needsVersion: body.needsVersion,
        });
      } else {
        setRejection({ message: e instanceof Error ? e.message : String(e) });
      }
    } finally {
      setBusy(false);
    }
  }

  if (models !== null && models.length === 0) {
    return (
      <Card title="Image hochladen">
        <EmptyState
          iconName="module"
          title="Erst ein Modell anlegen"
          description="Ein Image wird immer gegen ein Router-Modell geprüft — ohne Modell gibt es keinen Maßstab."
        />
      </Card>
    );
  }

  return (
    <Card
      title="Image hochladen"
      subtitle="Fertiges sysupgrade-Image aus dem Firmware-Repo. Version und Gerät liest der Server aus dem Image."
    >
      <div className="flex flex-col gap-4">
        {done && (
          <Alert variant="success" dismissible onDismiss={() => setDone(null)}>
            {done}
          </Alert>
        )}

        <DropZone file={file} onPick={pick} />

        <Felder
          models={models}
          modelId={modelId}
          onModel={(id) => {
            setModelId(id);
            setRejection(null);
          }}
          tag={tag}
          onTag={setTag}
          version={version}
          onVersion={setVersion}
          versionNoetig={!!rejection?.needsVersion}
        />

        <SignatureField signature={signature} onPick={setSignature} />

        {rejection && (
          <RejectionNotice rejection={rejection} busy={busy} onForce={() => void upload(true)} />
        )}

        <div className="flex flex-wrap justify-end gap-2">
          {file && (
            <Button variant="ghost" onClick={() => pick(null)}>
              Datei verwerfen
            </Button>
          )}
          <Button
            variant="primary"
            iconName="upload"
            loading={busy}
            disabled={!file || !modelId}
            onClick={() => void upload(false)}
          >
            Hochladen &amp; prüfen
          </Button>
        </div>
      </div>
    </Card>
  );
}
